import * as React from "react";
import {
  Alert,
  Box,
  Checkbox,
  Column,
  Columns,
  Heading,
  Hidden,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { Field, FieldProps, FormikProps, useFormikContext } from "formik";
import {
  AccountPayer as AccountPayerType,
  Contact,
  EnergyType,
  FormValues,
  GasOptionEnum,
} from "../types";
import AutocompleteField from "./AutocompleteField";
import { apiClient } from "src/api/apiService";
import { GasOptions } from "./GasOptions";
import { AppContext } from "src/context/AppContext";
import FormContext from "src/context/FormContext";
import ReadOnlyGasSectionInPremise from "../ReadOnlyGasSectionInPremise";
import FormFieldLayout from "./FormFieldLayout";
import { getIsNSWBorderPostcode } from "../helpers";

export type CollaboratorOption = {
  value: string;
  label: string;
};

export const mapGasOptionToLabel =
  (state: string, postcode: string) =>
  (gasOption: GasOptionEnum): string => {
    const isNSWBorder = getIsNSWBorderPostcode(postcode);
    const labels = {
      [GasOptionEnum.BOTH]: "gas",
      [GasOptionEnum.GAS_LINE]:
        state === "NSW" && !isNSWBorder ? "path valve" : "gas line",
      [GasOptionEnum.GAS_METER]:
        state === "NSW" && !isNSWBorder ? "meter kit" : "gas meter",
    };
    return labels[gasOption] || "gas";
  };

interface GasSectionProps {
  homeUser?: boolean;
  premiseId?: string | null;
}

const GasSectionInPremise: React.FC<GasSectionProps> = ({
  homeUser = false,
  premiseId,
}) => {
  const {
    formState: { gasServices },
  } = React.useContext(FormContext);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const {
    premiseAddress: { state, postcode },
    gasOptions,
  } = values;
  const showGasOptions = !!state && state !== "ACT";
  const disableFuelPayerSelection =
    homeUser &&
    !!premiseId &&
    values.selectedConnections.includes("Electricity" as EnergyType);

  React.useEffect(() => {
    if (state === "ACT") {
      setFieldValue("gasOptions", [GasOptionEnum.BOTH]);
    }
  }, [state]);

  return (
    <Stack
      space={{
        xs: "large",
        lg: "xlarge",
      }}
    >
      <Heading variant="h4">Gas</Heading>
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {showGasOptions ? <GasOptions /> : null}
        {gasOptions.map((option) =>
          gasServices?.[option]?.isReadOnly ? (
            <ReadOnlyGasSectionInPremise
              gasOption={option}
              key={option}
              homeUser={homeUser}
            />
          ) : homeUser ? (
            option !== GasOptionEnum.GAS_LINE && (
              <Stack
                space={{
                  xs: "xlarge",
                  md: "xxlarge",
                }}
                key={option}
              >
                <AccountPayer
                  gasOption={option}
                  gasOptions={gasOptions}
                  _mapGasOptionToLabel={mapGasOptionToLabel(state, postcode)}
                  homeUser={homeUser}
                  disableFuelPayerSelection={disableFuelPayerSelection}
                />
              </Stack>
            )
          ) : (
            <Stack
              space={{
                xs: "xlarge",
                md: "xxlarge",
              }}
              key={option}
            >
              <AccountPayer
                gasOption={option}
                gasOptions={gasOptions}
                _mapGasOptionToLabel={mapGasOptionToLabel(state, postcode)}
                homeUser={homeUser}
                disableFuelPayerSelection={disableFuelPayerSelection}
              />

              <CollaboratorOption
                gasOption={option}
                _mapGasOptionToLabel={mapGasOptionToLabel(state, postcode)}
              />
            </Stack>
          )
        )}
      </Stack>
    </Stack>
  );
};

export default GasSectionInPremise;

interface GasSubSectionProps {
  gasOption: GasOptionEnum;
  _mapGasOptionToLabel: (gasOption: GasOptionEnum) => string;
}

interface AccountPayerProps extends GasSubSectionProps {
  gasOptions: GasOptionEnum[];
  homeUser: boolean;
  disableFuelPayerSelection: boolean;
}

const AccountPayer: React.FC<AccountPayerProps> = ({
  gasOption,
  gasOptions,
  homeUser,
  _mapGasOptionToLabel,
  disableFuelPayerSelection,
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { requestingCompany } = values;
  const {
    formState: { gasServices },
  } = React.useContext(FormContext);

  React.useEffect(() => {
    // If there are saved gas options, set the account payer to the same as the first saved gas option
    const savedGasOptions = gasOptions.filter(
      (option) => option !== gasOption && gasServices?.[option]?.draftId
    );
    const isCurrentGasOptionSaved = gasServices?.[gasOption]?.draftId;
    if (savedGasOptions.length > 0 && !isCurrentGasOptionSaved) {
      setFieldValue(
        `${gasOption}.accountPayer`,
        values[savedGasOptions[0]].accountPayer
      );
    }
  }, []);

  return (
    <FormFieldLayout multipleFields>
      <Field id="account-payer" name={`${gasOption}.accountPayer`}>
        {({ field, meta }: { field: FormikProps<FieldProps>; meta: any }) => (
          <RadioGroup
            {...field}
            id="account-payer"
            name={`${gasOption}.accountPayer`}
            label={`Who's responsible for the ${_mapGasOptionToLabel(
              gasOption
            )} ${gasOption === GasOptionEnum.GAS_LINE ? "fee" : "account"}?`}
            error={meta.error}
            helperText={meta.error}
            value={values[gasOption].accountPayer}
            disabled={disableFuelPayerSelection}
          >
            <Radio
              id="my-company"
              label={requestingCompany ? requestingCompany.name : "My company"}
              value={AccountPayerType.COMPANY}
              helperText={
                gasOption === GasOptionEnum.GAS_LINE
                  ? `Will be responsible for any charges relating to the ${_mapGasOptionToLabel(
                      gasOption
                    )} connection.`
                  : "Will be responsible for any charges until the account is closed."
              }
            />

            <Radio
              id="customer"
              label="Customer"
              value={AccountPayerType.SOMEONE_ELSE}
              helperText={
                gasOption === GasOptionEnum.GAS_LINE
                  ? `Will be responsible for any charges relating to the ${_mapGasOptionToLabel(
                      gasOption
                    )} connection.`
                  : "Will be responsible for any charges."
              }
            />
          </RadioGroup>
        )}
      </Field>
      {!homeUser &&
        values?.[gasOption].accountPayer === AccountPayerType.COMPANY && (
          <Alert>
            <Text>
              {`I understand and agree that upon installation of gas for the supply address listed, it will be sold to me under the terms, conditions, and rates applicable to the multisite agreement in the welcome email. This includes any standard installation fee in addition to ongoing account charges.`}
            </Text>
          </Alert>
        )}
    </FormFieldLayout>
  );
};

const CollaboratorOption: React.FC<GasSubSectionProps> = ({
  gasOption,
  _mapGasOptionToLabel,
}) => {
  const {
    context: { user },
  } = React.useContext(AppContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const [collaborators, setCollaborators] = React.useState<
    CollaboratorOption[]
  >([]);
  const { requestingCompany } = values;
  const { assignRequirements, collaboratorId } = values[gasOption];

  const gasOptionLabel = _mapGasOptionToLabel(gasOption);

  React.useEffect(() => {
    const getCollaborators = async () => {
      setLoading(true);
      try {
        const collaboratorsResponse = await apiClient
          .get<Contact[]>(`/draft/collaborators/${requestingCompany?.id}`)
          .then((response) => response.data);

        const collaboratorOptions = collaboratorsResponse?.map((c) => ({
          label: `${c.first_name} ${c.last_name}`,
          value: c.id,
        }));

        const currentUserOption = {
          label: `${user?.first_name} ${user?.last_name}`,
          value: user?.id || "",
        };
        // Add current user to the list of collaborators if they are not already in the list
        // This is used for assignee to display their name when they open the request
        if (collaboratorId === user?.id) {
          setCollaborators([...collaboratorOptions, currentUserOption]);
        } else {
          setCollaborators(collaboratorOptions);
        }
      } catch (error) {
        console.log(`Fetch collaborators error: ${error}`);
      } finally {
        setLoading(false);
      }
    };
    if (requestingCompany?.id) {
      getCollaborators();
    }
  }, [requestingCompany?.id, collaboratorId, user?.id]);

  return (
    <Columns
      collapseBelow="md"
      space={{
        xs: "none",
        md: "xlarge",
      }}
      alignY="stretch"
    >
      <Column width="2/3">
        <Stack
          space={{
            xs: "large",
            md: "xlarge",
          }}
        >
          <Checkbox
            id={`${gasOption}.assignRequirements`}
            onChange={() => {
              setFieldValue(
                `${gasOption}.assignRequirements`,
                !assignRequirements
              );
              setFieldValue(`${gasOption}.collaboratorId`, "");
            }}
            label={`Assign ${gasOptionLabel} requirements`}
            checked={assignRequirements}
          />
          {assignRequirements ? (
            <Field
              component={AutocompleteField}
              name={`${gasOption}.collaboratorId`}
              label="Assigned to"
              options={collaborators}
              loading={loading}
            />
          ) : null}
        </Stack>
      </Column>
      {/* HelperText */}
      {assignRequirements ? (
        <Column width="1/3">
          <Box display="flex" alignItems="stretch" height="full">
            <Hidden below="md">
              {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
              <Box
                style={{ borderLeft: "1px solid #E3E3E3" }}
                paddingRight="medium"
                height="full"
              />
              {/* End Faux vertical <Divider> */}
            </Hidden>
            <Box alignSelf="center">
              <Text variant="body-small" tone="neutralLight">
                {`The person assigned to the gas request will see a draft in their
                “Tasks” section. You can reassign the request to someone else at
                any time, or complete and lodge it yourself.`}
              </Text>
            </Box>
          </Box>
        </Column>
      ) : null}
      {/* End HelperText */}
    </Columns>
  );
};
